// InspectionFormModal.jsx
import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Badge from './Badge';
import LoadingSpinner from './LoadingSpinner';
import { PlusIcon, CheckIcon, TrashIcon, LinkIcon } from '@heroicons/react/24/outline';


const WipFormModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  technicians,
  handleDelete,
  isEditing,
  disabled,
  loading
}) => {
  const [jobSiteQuery, setJobSiteQuery] = useState('');
  const [jobSiteResults, setJobSiteResults] = useState([]);
  const [isJobSiteDropdownVisible, setIsJobSiteDropdownVisible] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const [showLinkEditor, setShowLinkEditor] = useState(false);
  const [links, setLinks] = useState([]); // existing saved links
  const [pendingRows, setPendingRows] = useState([]); // temporary unsaved rows

  // Figure out the inspection_id this modal should save to
  // Adjust this if your modal passes a different field
  const inspectionId = formData?.inspection_id || formData?.id;

  // Load existing links when opening the modal or switching inspection
  useEffect(() => {
    const fetchLinks = async () => {
      if (!isOpen || !inspectionId) return;
      try {
        const { data } = await axios.get(`${apiUrl}/wip_crud.php`, {
          params: { getLinks: 1, inspection_id: inspectionId },
        });
        const ok = Array.isArray(data) && data[0] === 'success';
        setLinks(ok ? (data[1] || []) : []);
      } catch {
        // keep UI silent if none exist
        setLinks([]);
      }
    };

    fetchLinks();
  }, [isOpen, inspectionId, apiUrl]);

  // Reset editor when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowLinkEditor(false);
      setPendingRows([]);
    }
  }, [isOpen]);

  // Add a new empty pending row
  const addPendingRow = () => {
    setShowLinkEditor(true);
    setPendingRows(prev => [...prev, { label: '', link: '' }]);
  };

  // Update a pending row
  const updatePendingRow = (idx, field, value) => {
    setPendingRows(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  // Save a pending row
  const savePendingRow = async (idx) => {
    if (!inspectionId) {
      toast.error('Missing inspection id', { position: 'top-center' });
      return;
    }
    const row = pendingRows[idx];
    if (!row.label?.trim() || !row.link?.trim()) {
      toast.error('Title and Link are required.', { position: 'top-center' });
      return;
    }
    try {
      const payload = {
        status: 'addLink',
        data: {
          link_id: null,
          inspection_id: inspectionId,
          label: row.label.trim(),
          link: row.link.trim()
        }
      };
      const { data } = await axios.post(`${apiUrl}/wip_crud.php`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      console.log(data);

      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;
      if (ok) {
        // New link id from either array resp or object resp
        const newId = Array.isArray(data) ? (data[2]?.id ?? data[1]?.id) : data?.id;
        setLinks(prev => [...prev, { id: newId, inspection_id: inspectionId, label: row.label.trim(), link: row.link.trim() }]);
        // remove that pending row
        setPendingRows(prev => prev.filter((_, i) => i !== idx));
        toast.success('Link saved', { position: 'top-center' });
      } else {
        toast.error((Array.isArray(data) && data[1]) || data?.error || 'Failed to save link', { position: 'top-center' });
      }
    } catch (e) {
      toast.error('Error saving link', { position: 'top-center' });
    }
  };

  // Delete a saved link
  const deleteSavedLink = async (id) => {
    try {
      const payload = { status: 'deleteLink', data: { id } };
      const { data } = await axios.post(`${apiUrl}/wip_crud.php`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;
      if (ok) {
        setLinks(prev => prev.filter(l => l.id !== id));
        toast.success('Link deleted', { position: 'top-center' });
      } else {
        toast.error((Array.isArray(data) && data[1]) || data?.error || 'Failed to delete link', { position: 'top-center' });
      }
    } catch {
      toast.error('Error deleting link', { position: 'top-center' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

    useEffect(() => {
    // Whenever the modal closes, reset the child’s internal search states
    if (!isOpen) {
      setJobSiteQuery('');
      setJobSiteResults([]);
      setIsJobSiteDropdownVisible(false);
    }
  }, [isOpen]);


  const handleJobSiteInputChange = (e) => {
    const value = e.target.value;
    setJobSiteQuery(value);
    
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    if (value.length >= 3) {
      const timeout = setTimeout(async () => {
        // Fetch job sites from the backend
        try {
          const response = await axios.get(`${apiUrl}/wip_crud.php`, {
            params: { getSchedulerJobSites: value },
          });
          const results = Array.isArray(response.data) ? response.data : [];
          setJobSiteResults(results);
          setIsJobSiteDropdownVisible(true);
        } catch (error) {
          // Handle errors
          console.error('Error fetching job sites:', error);
        }
      }, 500); // Delay of 500ms

      setDebounceTimeout(timeout);
    } else {
      setJobSiteResults([]);
      setIsJobSiteDropdownVisible(false);
    }
  };

  const handleJobSiteSelect = (jobSite) => {
    setJobSiteQuery(jobSite.job_site); // Display the selected job site's address
    setIsJobSiteDropdownVisible(false);
    setFormData((prevData) => ({
      ...prevData,
      // id: jobSite.id,
      job_site: jobSite.job_site,
      job_number: jobSite.job_number
    }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20"
        onClose={onClose}
      >
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        {/* Modal content */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/30 backdrop-blur-sm">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
            <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 bg-purple-400 px-6 py-4">
                <Dialog.Title className="text-xl font-semibold text-gray-900">
                  {formData.id ? 'Edit WIP Schedule' : 'New WIP Schedule'}
                </Dialog.Title>

                {formData.tentative ? (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-600/20">
                    Tentative
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-600/20">
                    Active
                  </span>
                )}
              </div>

              {/* Body */}
              <form id="wipForm" onSubmit={onSubmit} className="px-6 py-5 space-y-6">
                {/* Tentative checkbox */}
                <div>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="tentative"
                      checked={!!formData.tentative}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-red-400 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm font-semibold text-red-600">Tentative</span>
                  </label>
                </div>

                {/* Status Description (only if tentative) */}
                {formData.tentative ? (
                  <div>
                    <label className="block text-sm font-medium text-red-700">
                      Status Description
                    </label>
                    <textarea
                      name="status_description"
                      value={formData.status_description || ''}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-gray-900 focus:border-red-500 focus:ring-red-500"
                      placeholder="Explain why this job is tentative..."
                      required
                    />
                  </div>
                ) : null}

                {/* Job site / number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Job Number
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="job_site"
                      value={jobSiteQuery ? jobSiteQuery : formData.schedule_id ? formData.job_site : ''}
                      onChange={handleJobSiteInputChange}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                    {/* Dropdown */}
                        {/* Dropdown List */}
                        {isJobSiteDropdownVisible && jobSiteResults.length > 0 && (
                          <ul className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-auto divide-y-2">
                            {jobSiteResults.map((jobSite) => {
                              // decide li background
                              const liBgClass = jobSite.status === 0 
                                ? 'bg-red-100 hover:bg-red-200'  // you can tweak the shades
                                : 'hover:bg-gray-100';

                              return (
                                <li
                                  key={jobSite.job_number}
                                  className={`${liBgClass} px-4 py-2 cursor-pointer`}
                                  onClick={() => handleJobSiteSelect(jobSite)}>
                                  {jobSite.project_hold_amount && (
                                    <div className='text-red-800 p-1 text-xs'>********** Project On Hold **********</div>
                                  )}
                                  <div className="text-sm font-medium text-blue-900">
                                    {jobSite.hold === 1 && (
                                      <Badge color="pink" feed="On Hold" />
                                    )}
                                    <span className="bg-yellow-50 p-1">
                                    {jobSite.customer_name}
                                    </span>{' '}
                                    | {jobSite.f_p_type} | {jobSite.type}
                                  </div>
                                  <div className="font-medium">
                                    {jobSite.job_site}
                                  </div>
                                  <div className="text-sm text-green-700">
                                    Job#: {jobSite.job_number}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                  </div>
                </div>

                {/* Date & time fields in grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Schedule Date
                    </label>
                    <input
                      type="date"
                      name="schedule_from"
                      value={formData.schedule_from}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Time In
                    </label>
                    <input
                      type="time"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <input
                      type="date"
                      name="schedule_to"
                      value={formData.schedule_to || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Time Out
                    </label>
                    <input
                      type="time"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>

                {/* Technician dropdowns */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Lead Technician
                    </label>
                    <select
                      name="tech_id"
                      value={formData.tech_id || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    >
                      <option value="">Select Technician</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.first_name} {tech.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Additional Technician
                    </label>
                    <select
                      name="additional_tech_id"
                      value={formData.additional_tech_id}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="">Additional Tech</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.first_name} {tech.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Instructions / Notes
                  </label>
                  <textarea
                    name="user_comment"
                    value={formData.user_comment}
                    onChange={handleInputChange}
                    rows={4}
                    className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>

                {/* --- Links Editor --- */}
                {showLinkEditor && (
                  <div className="rounded-xl border border-gray-200 bg-white/70 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <LinkIcon className="h-4 w-4" /> Links
                      </h4>
                      <button
                        type="button"
                        onClick={addPendingRow}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      >
                        <PlusIcon className="h-4 w-4" /> Add
                      </button>
                    </div>

                    {/* Existing saved links */}
                    {links.length > 0 && (
                      <ul className="mb-3 space-y-2">
                        {links.map((l) => (
                          <li key={l.id} className="flex items-center gap-2 rounded-lg bg-gray-50 p-2">
                            <span className="truncate text-sm font-medium text-gray-900">{l.label}</span>
                            <span className="mx-1 text-gray-300">•</span>
                              <a
                                href={l.link.startsWith('http') ? l.link : `https://${l.link}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="truncate text-sm text-indigo-600 hover:underline"
                              >
                                {l.link}
                              </a>
                            <button
                              type="button"
                              onClick={() => deleteSavedLink(l.id)}
                              className="ml-auto inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-600/20 hover:bg-red-100"
                              title="Delete link"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Pending (unsaved) rows */}
                    {pendingRows.length > 0 && (
                      <div className="space-y-2">
                        {pendingRows.map((row, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2">
                            <input
                              type="text"
                              placeholder="Title"
                              value={row.label}
                              onChange={(e) => updatePendingRow(idx, 'label', e.target.value)}
                              className="col-span-4 rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                            <input
                              type="url"
                              placeholder="https://link"
                              value={row.link}
                              onChange={(e) => updatePendingRow(idx, 'link', e.target.value)}
                              className="col-span-6 rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                            <div className="col-span-2 flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => savePendingRow(idx)}
                                className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-2.5 py-2 text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                title="Save"
                              >
                                <CheckIcon className="h-5 w-5" />
                              </button>
                              <button
                                type="button"
                                onClick={addPendingRow}
                                className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-2.5 py-2 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                title="Add another"
                              >
                                <PlusIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Empty state hint */}
                    {links.length === 0 && pendingRows.length === 0 && (
                      <p className="text-xs text-gray-500">No links yet. Click <strong>Add</strong> to create one.</p>
                    )}
                  </div>
                )}

              </form>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
                {isEditing && (
                  <>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Delete
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowLinkEditor(v => !v);
                        if (pendingRows.length === 0 && links.length === 0) addPendingRow();
                      }}
                      className="inline-flex items-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      {showLinkEditor ? 'Hide Links' : 'Show Links'}
                    </button>
                  </>
                )}

                <button type="button" onClick={onClose} className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  Cancel
                </button>
                <button type="submit" form="wipForm" className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {formData.id ? 'Update' : 'Create'}
                </button>
              </div>

            </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WipFormModal;
